import { useEffect, useState } from 'react'
import { zodResolver } from '@hookform/resolvers/zod'
import { Button, Checkbox, DatePicker, Input, Select } from '@iveco-ti/iveco-ds'
import { FormProvider, useForm } from '@iveco-ti/iveco-ds/hook-form'
import { HeadProvider, Title } from 'react-head'
import { useTranslation } from 'react-i18next'

import { GraphicArea } from '@/common/components/GraphicArea'
import { useGetUserInfo } from '@/common/hooks/useGetUserInfo'
import { Gender } from '@/common/models/person'
import { formatCPF } from '@/common/utils/formatCPF'
import { getLocale } from '@/common/utils/getLocale'
import { translateGender } from '@/common/utils/translateGender'
import { GeneralDataFormSkeleton } from './components/GeneralDataFormSkeleton'
import { useEditProfile } from './hooks/useEditProfile'
import {
  ProfileGeneralDataForm,
  ProfileGeneralDataSchema,
} from './schemas/ProfileGeneralDataSchema'

export default function GeneralData() {
  const [isEditing, setIsEditing] = useState(false)

  const { t } = useTranslation(['profile', 'common'])
  const { handleEditProfile, isPendingEditProfile } = useEditProfile()
  const hookForm = useForm<ProfileGeneralDataForm>({
    resolver: zodResolver(ProfileGeneralDataSchema()),
  })
  const { userInfo, isPendingUserInfo } = useGetUserInfo()
  const isForeignValue = hookForm.watch('isForeign')
  const personLabel = isForeignValue
    ? t('general_data.identification_document.label')
    : t('general_data.document_number.label')
  const personPlaceholder = isForeignValue
    ? t('general_data.identification_document.placeholder')
    : t('general_data.document_number.placeholder')

  const onSubmit = async (data: ProfileGeneralDataForm) => {
    if (!userInfo) return

    const cleanDocumentNumber = data.documentNumber.replace(/\D/g, '')

    const payload = {
      birthDate: data.birthDate?.toISOString(),
      documentNumber: cleanDocumentNumber,
      name: data.fullName,
      genre: data.gender,
      isForeign: data.isForeign,
    }

    await handleEditProfile(userInfo?.personId, payload)

    setIsEditing(false)
  }

  const GENDER_OPTIONS = Object.values(Gender).map((gender) => ({
    label: translateGender(t, gender),
    value: gender,
  }))

  useEffect(() => {
    if (userInfo?.Person) {
      hookForm.reset({
        ...userInfo,
        ...userInfo.Person,
        birthDate: userInfo.Person?.birthDate
          ? new Date(userInfo.Person.birthDate)
          : null,
        fullName: userInfo.Person?.name,
        gender: userInfo.Person?.genre,
        isForeign: userInfo.Person?.isForeign || false,
        documentNumber:
          userInfo.Person?.documentNumber && !userInfo.Person?.isForeign
            ? formatCPF(userInfo.Person.documentNumber)
            : userInfo.Person?.documentNumber || '',
      })
    }
  }, [userInfo, hookForm])

  useEffect(() => {
    const currentValue = hookForm.getValues('documentNumber')
    if (currentValue) {
      if (isForeignValue) {
        hookForm.setValue('documentNumber', currentValue.replace(/\D/g, ''))
      } else {
        const cleanValue = currentValue.replace(/\D/g, '')
        if (cleanValue.length === 11) {
          hookForm.setValue('documentNumber', formatCPF(cleanValue))
        }
      }
    }
  }, [isForeignValue, hookForm])

  if (isPendingUserInfo) {
    return <GeneralDataFormSkeleton />
  }

  return (
    <>
      <HeadProvider>
        <Title>{t('profile:general_data.browser.title')}</Title>
      </HeadProvider>

      <GraphicArea.Root>
        <GraphicArea.Header>
          <h3 className="text-2xl font-semibold text-brand-800">
            {t('general_data.title')}
          </h3>
        </GraphicArea.Header>

        <FormProvider {...hookForm}>
          <GraphicArea.Body>
            <form className="flex flex-col gap-6 justify-between">
              <div className="max-h-full overflow-y-auto">
                <div className="flex flex-col w-full gap-5">
                  <div className="flex flex-col sm:flex-row w-full gap-5">
                    <div className="w-full sm:w-1/2">
                      <Input.Base
                        disabled={!isEditing}
                        name="fullName"
                        label={t('general_data.full_name.label')}
                        placeholder={t('general_data.full_name.placeholder')}
                      />
                    </div>

                    <div className="w-full sm:w-1/2">
                      {!isForeignValue ? (
                        <Input.Mask
                          mask="###.###.###-##"
                          disabled={!isEditing}
                          name="documentNumber"
                          label={personLabel}
                          placeholder={personPlaceholder}
                        />
                      ) : (
                        <Input.Base
                          disabled={!isEditing}
                          name="documentNumber"
                          label={personLabel}
                          placeholder={personPlaceholder}
                        />
                      )}
                      <div className="mt-2">
                        <Checkbox
                          disabled={!isEditing}
                          name="isForeign"
                          text={t('general_data.is_foreign.label')}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-col sm:flex-row w-full gap-5">
                    <div className="w-full sm:w-1/2">
                      <DatePicker.Single
                        disabled={!isEditing}
                        locale={getLocale()}
                        name="birthDate"
                        label={t('general_data.birthdate.label')}
                        placeholder={t('general_data.birthdate.placeholder')}
                      />
                    </div>

                    <div className="w-full sm:w-1/2">
                      <Select.Single
                        disabled={!isEditing}
                        options={GENDER_OPTIONS}
                        name="gender"
                        label={t('general_data.gender.label')}
                        placeholder={t('general_data.gender.placeholder')}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </form>
          </GraphicArea.Body>

          <GraphicArea.Footer className="flex flex-row justify-end">
            {isEditing && (
              <div className="flex flex-row w-full justify-end gap-2">
                <Button.Root
                  type="button"
                  disabled={isPendingEditProfile}
                  variant="secondary"
                  onClick={() => {
                    setIsEditing(!isEditing)
                    hookForm.reset()
                  }}
                >
                  {t('actions.cancel_button.text')}
                </Button.Root>

                <Button.Root onClick={hookForm.handleSubmit(onSubmit)}>
                  {t('actions.save_button.text')}
                </Button.Root>
              </div>
            )}

            {!isEditing && (
              <Button.Root
                type="button"
                onClick={() => setIsEditing(!isEditing)}
              >
                {t('actions.edit_button.text')}
              </Button.Root>
            )}
          </GraphicArea.Footer>
        </FormProvider>
      </GraphicArea.Root>
    </>
  )
}
